import React, { useEffect, useRef, useState } from 'react';
import { Alert, Card, Space, Spin, Typography } from 'antd';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-dist/swagger-ui.css';

import { fetchAuthSession } from '../auth/gateway';
import { API_CONFIG } from '../config/api';
import { secureLogger } from '../utils/secureLogger';

const { Paragraph, Text, Title } = Typography;

const INTERNAL_OPENAPI_URL = `${API_CONFIG.BASE_URL}/internal/openapi.json`;

/**
 * Cognito 認証済みユーザー専用の API ドキュメント画面。
 * backend の protected OpenAPI schema を Swagger UI で表示する。
 */
const InternalApiDocsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const initializeSwagger = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        if (!token) {
          throw new Error('Cognito access token を取得できませんでした。');
        }
        tokenRef.current = token;
        if (!cancelled) {
          setErrorMessage(null);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'API ドキュメントの読み込みに失敗しました。';
        secureLogger.error('Failed to initialize internal API docs', { error: message });
        if (!cancelled) {
          setErrorMessage(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    initializeSwagger();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Title level={3} style={{ margin: 0 }}>
            APIドキュメント
          </Title>
          <Paragraph style={{ marginBottom: 0 }}>
            Cognito 認証済みユーザーだけが閲覧できる内部向け API ドキュメントだよ。公開の
            <Text code> /docs </Text>
            と
            <Text code> /redoc </Text>
            は閉じてある。
          </Paragraph>
          <Text type="secondary">
            OpenAPI schema は backend の protected endpoint から Bearer 付きで取得している。
          </Text>
        </Space>
      </Card>

      {loading && (
        <Card>
          <Space align="center">
            <Spin />
            <Text>Swagger UI を初期化しています...</Text>
          </Space>
        </Card>
      )}

      {errorMessage && (
        <Alert
          type="error"
          showIcon
          message="API ドキュメントを表示できませんでした"
          description={errorMessage}
        />
      )}

      <Card bodyStyle={{ padding: 0 }}>
        {!loading && tokenRef.current && (
          <SwaggerUI
            url={INTERNAL_OPENAPI_URL}
            deepLinking
            displayRequestDuration
            docExpansion="list"
            filter
            requestInterceptor={(request) => {
              const headers = (request.headers ?? {}) as Record<string, string>;
              headers.Authorization = `Bearer ${tokenRef.current}`;
              request.headers = headers;
              return request;
            }}
          />
        )}
      </Card>
    </Space>
  );
};

export default InternalApiDocsPage;
